import { supabase } from './client';

export interface EntityProfile {
  id: string;
  name: string;
  phone: string | null;
  address: string | null;
  type: 'new' | 'existing';
  owner_id: string;
  created_at: string;
  updated_at: string;
}

export async function getEntity() {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.log('No authenticated user found');
      return null;
    }

    console.log('useEntity: Loading entity for user:', user.id);

    // Get the first (oldest) entity owned by the user
    const { data: entities, error: entitiesError } = await supabase
      .from('entities')
      .select('*')
      .eq('owner_id', user.id)
      .order('created_at', { ascending: true })
      .limit(1);

    if (entitiesError) {
      console.error('Error fetching entity:', entitiesError);
      return null;
    }
    
    // Return the first entity or null if none found
    return entities && entities.length > 0 ? entities[0] : null;
  } catch (error) {
    console.error('Failed to get entity:', error);
    return null;
  }
}

export async function createDefaultEntity() {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Check if entity already exists
    const existingEntity = await getEntity();
    if (existingEntity) {
      console.log('Using existing entity:', existingEntity.id);
      return existingEntity;
    }

    console.log('Creating new default entity for user:', user.id);
    // Create new default entity for the user
    const { data: entity, error } = await supabase
      .from('entities')
      .insert([{
        name: 'My Business', 
        type: 'new',
        status: 'active',
        owner_id: user.id
      }])
      .select()
      .single();

    if (error) {
      console.error('Failed to create default entity:', error);
      throw error;
    }

    // Create entity_members entry for the owner
    const { error: memberError } = await supabase
      .from('entity_members')
      .insert([{
        entity_id: entity.id,
        user_id: user.id,
        role: 'owner'
      }]);

    if (memberError) {
      console.error('Failed to create entity member:', memberError);
      // Continue even if this fails - at least the entity exists
    }

    return entity;
  } catch (error) {
    console.error('Create entity error:', error);
    throw error;
  }
}

export async function updateEntityProfile(
  id: string,
  updates: Partial<Omit<EntityProfile, 'id' | 'owner_id' | 'created_at' | 'updated_at'>>
) {
  const { data: profile, error } = await supabase
    .from('organization_profiles')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return profile;
}

export async function createEntityProfile(
  data: Omit<EntityProfile, 'id' | 'created_at' | 'updated_at' | 'owner_id'>
) {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error('User not authenticated');
  }

  const { data: profile, error } = await supabase
    .from('organization_profiles')
    .insert([{ ...data, owner_id: user.id }])
    .select()
    .single();

  if (error) {
    console.error('Failed to create entity profile:', error);
    throw error;
  }

  return profile;
}